// Outstanding / unanswered question helpers shared by the admin vendor-detail
// tabs and the vendor post-submit view. completionByQuestion (from
// computeProgress) only contains visible questions; a non-null CompletionReason
// means incomplete — the exact signal that drives the X/Y progress bar.

import type { CompletionReason } from './visibility.ts';
import { frozenApprovedDoc } from './frozenApprovedDoc.ts';

type CountableQuestion = {
  id: string;
  required: boolean;
  exclude_from_compliance: boolean;
  // Compliance-doc flag: a frozen renewal row (see below) is only possible on
  // a compliance doc. Optional so non-admin callers (postSubmitLocked) need
  // not supply it.
  include_in_compliance_documents?: boolean;
};

type CountableSubmission = {
  status: string;
  // 'admin' covers admins and reviewers (both post comments as 'admin'). When
  // they have the last word the item is awaiting the vendor, so it is not an
  // outstanding admin action; null (no thread) or 'vendor' means the admin
  // still owns it.
  latest_comment_author?: 'admin' | 'vendor' | null;
  // A resubmit newer than the last review is the vendor acting even without a
  // comment — it keeps the item counted. Mirrors admin/awaitingReply.ts.
  resubmitted_at?: string | null;
  reviewed_at?: string | null;
  // Present (admin endpoint) when this compliance doc's approved version was
  // later replaced: the questionnaire row is frozen on the original and its
  // renewal is reviewed in the Compliance Documents tab, so it is inert here.
  approved_files?: unknown[] | null;
  // Renewal re-verification marker — part of the freeze condition (a frozen row
  // is one whose renewal is pending reverify or re-approved).
  reverify_requested_at?: string | null;
};

// A required, visible question the vendor still owes an answer for.
// exclude_from_compliance questions are not counted (they sit outside the
// progress percentage), matching the progress bar.
export function isUnansweredRequired(
  question: CountableQuestion,
  completionByQuestion: Map<string, CompletionReason | null>,
): boolean {
  return (
    question.required &&
    !question.exclude_from_compliance &&
    completionByQuestion.get(question.id) != null
  );
}

// Items still needing attention in a tab: submissions awaiting a decision
// (pending/rejected, unless the ball is with the vendor — see below) PLUS
// required, visible, incomplete questions the vendor hasn't answered. Counted
// once per question — a pending/rejected sub wins and skips the incomplete check.
export function outstandingActionCount(
  questions: CountableQuestion[],
  completionByQuestion: Map<string, CompletionReason | null>,
  subByQ: Map<string, CountableSubmission>,
  // skipFrozen: for QUESTIONNAIRE-tab badges, a frozen renewal row is inert
  // (its action lives in the Compliance Documents tab). The Compliance
  // Documents tab badge itself must NOT skip it — that is exactly where the
  // renewal awaits review — so it calls with the default (false).
  opts?: { skipFrozen?: boolean },
): number {
  const skipFrozen = opts?.skipFrozen ?? false;
  let n = 0;
  for (const q of questions) {
    const sub = subByQ.get(q.id);
    if (skipFrozen && frozenApprovedDoc(sub, q.include_in_compliance_documents ?? false).frozen) {
      continue;
    }
    if (sub?.status === 'pending' || sub?.status === 'rejected') {
      // Count while the admin still owns the item: a fresh submission awaiting
      // review, a vendor reply, or a vendor resubmit. Exclude only when the
      // admin/reviewer replied last AND the vendor hasn't acted since (no
      // resubmit newer than the last review) — then the ball is with the
      // vendor. Mirrors the per-row "Reply needed" signal in
      // admin/awaitingReply.ts (kept in sync by hand — not imported, since this
      // module is also used by the vendor post-submit view).
      const adminRepliedLast = sub.latest_comment_author === 'admin';
      const vendorActedSinceReview =
        sub.resubmitted_at != null &&
        (sub.reviewed_at == null || sub.reviewed_at < sub.resubmitted_at);
      if (!adminRepliedLast || vendorActedSinceReview) n += 1;
      continue;
    }
    if (isUnansweredRequired(q, completionByQuestion)) n += 1;
  }
  return n;
}

// Whether a question's inputs should be read-only in the vendor post-submit
// view. Editable when the admin sent it back (changes + rejected) OR it is a
// stranded required+visible+unanswered question; locked otherwise.
export function postSubmitLocked(
  question: CountableQuestion,
  companyStatus: string | undefined,
  submission: { status: string } | undefined,
  completionByQuestion: Map<string, CompletionReason | null>,
): boolean {
  const editable =
    (companyStatus === 'changes' && submission?.status === 'rejected') ||
    isUnansweredRequired(question, completionByQuestion);
  return !editable;
}
